import { APP_INITIALIZER, NgModule } from '@angular/core';
import { AsyncPipe, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PlatformModule } from '@angular/cdk/platform';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';

import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatStepperModule } from '@angular/material/stepper';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { FileUploadModule } from 'ng2-file-upload';

import { AngularFireMessagingModule } from '@angular/fire/messaging';
import { AngularFireDatabaseModule } from '@angular/fire/database';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire';

import { CoreModule } from './core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { NgxMaskModule } from 'ngx-mask';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { RecaptchaModule, RecaptchaFormsModule, RECAPTCHA_LANGUAGE, RECAPTCHA_BASE_URL } from 'ng-recaptcha';
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { WebcamModule } from 'ngx-webcam';
import { NgxLoadingXConfig, NgxLoadingXModule, POSITION, SPINNER } from 'ngx-loading-x';

import { environment } from '@environments/environment';

import { OrgaoEmissorRgService } from './shared/services/orgao-emissor-rg.service';
import { MensagemService } from './shared/services/mensagem.service';
import { CensoService } from './shared/services/censo.service';
import { DependenteService } from './shared/services/dependente.service';
import { LocalidadeService } from './shared/services/localidade.service';
import { EscolaridadeService } from './shared/services/escolaridade.service';
import { MessagingService } from './shared/services/messaging.service';

import { HeaderComponent, HeaderPagesComponent } from '@app/shared/components/index';

import { AppComponent } from './app.component';
import {
  // public
  HomeComponent,
  EntrarComponent,
  DocumentacaoComponent,
  AcessoInternoComponent,

  // private
  AgendamentoComponent,
  PrimeiroPassoComponent,
  SegundoPassoComponent,
  TerceiroPassoComponent,
  AcompanhamentoComponent,
  ContestacaoComponent,
  ModalAvaliarAtendimentoComponent
} from '@pages/index';

import { FooterComponent } from './shared/components/footer/footer.component';

// imports segundo passo
import { AdicionarDependenteComponent } from '@pages/private/recadastramento/segundo-passo';
import { ExcluirDependenteComponent } from '@pages/private/recadastramento/segundo-passo';

import { LoaderComponent } from './shared/components/loader/loader.component';
import { LoadingUploadComponent } from './shared/components/loading-upload/loading-upload.component';

import { ModalWebcamComponent } from './pages/private/recadastramento/primeiro-passo/modal-webcam/modal-webcam.component';
import { ModalDocumentosComponent } from './pages/private/recadastramento/primeiro-passo/modal-documentos/modal-documentos.component';
import { ValidarObrigatorioComponent } from './shared/components/validar-obrigatorio/validar-obrigatorio.component';
import { ValidarMaxlengthComponent } from './shared/components/validar-maxlength/validar-maxlength.component';
import { ModalIdentidadeComponent } from './pages/private/recadastramento/primeiro-passo/modal-identidade/modal-identidade.component';
import { HeaderPublicPageComponent } from './shared/components/header-public-page/header-public-page.component';
import { MeusAgendamentosComponent } from './shared/components/agendamentos/meus-agendamentos/meus-agendamentos.component';
import { CadastrarAgendamentoComponent } from './shared/components/agendamentos/cadastrar-agendamento/cadastrar-agendamento.component';
import { EditarAgendamentoComponent } from './shared/components/agendamentos/editar-agendamento/editar-agendamento.component';
import { FormDocumentosComponent } from './pages/private/recadastramento/segundo-passo/form-documentos/form-documentos.component';
import { FormDadosPessoaisComponent } from './pages/private/recadastramento/segundo-passo/form-dados-pessoais/form-dados-pessoais.component';
import { FormEnderecoComponent } from './pages/private/recadastramento/segundo-passo/form-endereco/form-endereco.component';
import { FormContatosComponent } from './pages/private/recadastramento/segundo-passo/form-contatos/form-contatos.component';
import { FormDependentesComponent } from './pages/private/recadastramento/segundo-passo/form-dependentes/form-dependentes.component';
import { InformacoesAndamentoComponent } from './shared/components/informacoes-andamento/informacoes-andamento.component';
import { MatIconModule } from '@angular/material/icon';
import { OtpSmsComponent } from './pages/private/recadastramento/segundo-passo/otp-sms/otp-sms.component';
import { GerenciarRecadastramentoService } from './shared/services/gerenciar-recadastramento.service';
import { FormDadosFuncionaisComponent } from './pages/private/recadastramento/segundo-passo/form-dados-funcionais/form-dados-funcionais.component';
import { AdicionarTempoRppsComponent } from './pages/private/recadastramento/segundo-passo/adicionar-tempo-rpps/adicionar-tempo-rpps.component';
import { DirectivesModule } from './shared/diretivas/directives.module';
import { ValidarDataComponent } from './shared/components/validar-data/validar-obrigatorio.component';
import { ModalVerDocumentoComponent } from './pages/private/recadastramento/terceiro-passo/modal-ver-documento/modal-ver-documento.component';

const ngxLoadingXConfig: NgxLoadingXConfig = {
  show: false,
  bgBlur: 7,
  bgOpacity: 0.7,
  bgLogoUrl: '',
  bgLogoUrlPosition: POSITION.topLeft,
  bgLogoUrlSize: 0,
  spinnerType: SPINNER.threeStrings,
  bgColor: '#fff',
  spinnerSize: 180,
  spinnerColor: '#333',
  spinnerPosition: POSITION.centerCenter,
};

export function loadConfig(): () => Promise<void> {
  return () => fetch('assets/config/config.json')
    .then(res => res.json())
    .then((config: any) => {
      environment.cliente = config.cliente;
    })
    .catch(err => {
      console.error('Erro ao carregar config.json', err);
      return Promise.resolve(); // evita quebrar a inicialização
    });
}

@NgModule({
  declarations: [
    AppComponent,
    // Components
    HeaderComponent,
    HeaderPagesComponent,
    LoaderComponent,
    LoadingUploadComponent,
    ValidarObrigatorioComponent,
    ValidarMaxlengthComponent,
    ValidarDataComponent,
    // Public Pags
    HomeComponent,
    EntrarComponent,
    DocumentacaoComponent,
    // Private Pages
    AgendamentoComponent,
    // primeiro-passo
    PrimeiroPassoComponent,
    ModalWebcamComponent,
    ModalDocumentosComponent,
    ModalIdentidadeComponent,

    // segundo-passo
    SegundoPassoComponent,
    AdicionarDependenteComponent,
    FormDocumentosComponent,
    FormDadosPessoaisComponent,
    FormDadosFuncionaisComponent,
    AdicionarTempoRppsComponent,
    OtpSmsComponent,
    ExcluirDependenteComponent,

    // terceiro-passo
    TerceiroPassoComponent,

    // acompanhamento
    AcompanhamentoComponent,
    ContestacaoComponent,
    HeaderPublicPageComponent,
    AcessoInternoComponent,
    FooterComponent,
    FormEnderecoComponent,
    FormContatosComponent,
    FormDependentesComponent,
    InformacoesAndamentoComponent,
    ModalAvaliarAtendimentoComponent,
    ModalVerDocumentoComponent,

    // agendamento
    MeusAgendamentosComponent,
    CadastrarAgendamentoComponent,
    EditarAgendamentoComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    HttpClientModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatGridListModule,
    MatSelectModule,
    MatTabsModule,
    MatCardModule,
    MatIconModule,
    MatRadioModule,
    MatDividerModule,
    SweetAlert2Module.forRoot(),
    NgxMaskModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    BrowserAnimationsModule,
    WebcamModule,
    PlatformModule,
    FormsModule,
    FileUploadModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    NgxLoadingXModule.forRoot(ngxLoadingXConfig),
    MatDialogModule,
    MatStepperModule,
    AngularFireDatabaseModule,
    AngularFireAuthModule,
    AngularFireMessagingModule,
    AngularFireModule.initializeApp(environment.firebase),
    DirectivesModule
  ],
  providers: [
    MessagingService,
    AsyncPipe,
    LocalidadeService,
    EscolaridadeService,
    OrgaoEmissorRgService,
    DependenteService,
    CensoService,
    MensagemService,
    GerenciarRecadastramentoService,
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MatDialogRef, useValue: {} },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always', hideRequiredMarker: true } },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: '6LfsRKAUAAAAAAg_yJZsBzYaf0M_STpDHiT4kj3-'
      } as RecaptchaSettings
    },
    {
      provide: RECAPTCHA_LANGUAGE,
      useValue: 'pt'
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfig,
      multi: true
    }
  ],
  bootstrap: [AppComponent],
  entryComponents: [ModalWebcamComponent],
  exports: [MatInputModule, MatStepperModule]
})
export class AppModule { }
